/**
 * crmOpportunityNextStep — the Opportunity-side counterpart to crmLeadNextStep.js. Same shape, same
 * spirit: given an opportunity + its interactions (newest first) + the countersign status of its
 * active proposal (if any), returns the single recommended next action with an urgency color. Pure,
 * no React, no I/O — reuses the SAME `URGENCY` color map as Leads so the two engines read as one
 * system, not two.
 *
 * Deliberately does NOT depend on any Airtable rollup/formula for "days since activity" (unlike
 * Leads' `Days Since Last Interaction` field) — the caller supplies real interaction rows and this
 * computes recency itself. See the "no Airtable computed-field deps" note in
 * docs/MIGRATION_DERIVATIONS.md.
 */
import { LOST_STAGES } from '../../services/opportunities.js'

// Stage -> suggested Opportunity Activity Type for the "log a follow-up" prefill.
const STAGE_ACTIVITY = {
  'Business Development': 'Information Gathering',
  'Gathering Info': 'Information Gathering',
  'Requested': 'Information Gathering',
  'Under Review': 'Client Questions/Clarifications',
  'Negotiation': 'Terms Discussed (Negotiation)',
  'Approved': 'Follow-Up',
}

function daysSince(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (isNaN(t)) return null
  return Math.floor((Date.now() - t) / 86400000)
}

export function opportunityNextStep(opp, interactions, countersignStatus) {
  const stage = opp && opp.stage
  const ix = Array.isArray(interactions) ? interactions : []

  if (LOST_STAGES.indexOf(stage) !== -1) {
    const reason = opp.raw && opp.raw['Loss Reason']
    return {
      step: 'closed', title: 'Opportunity closed',
      desc: reason ? `Reason: ${reason}.` : 'This opportunity is no longer being pursued.',
      urgency: 'gray', action: null,
    }
  }

  if (stage === 'Executed') {
    return {
      step: 'won', title: 'Won — now a live project',
      desc: 'This opportunity has been fully executed and is running as a project.',
      urgency: 'teal', action: null,
    }
  }

  if (stage === 'Pending Countersignature') {
    const signedAt = countersignStatus && countersignStatus.clientSignedAt
    const days = signedAt ? daysSince(signedAt) : null
    const overdue = days !== null && days > 3
    return {
      step: 'countersign',
      title: overdue ? `Overdue: countersign now — signed ${days} day${days === 1 ? '' : 's'} ago` : 'Countersign to finalize',
      desc: (countersignStatus && countersignStatus.clientSignerName)
        ? `${countersignStatus.clientSignerName} signed${days !== null ? ` ${days} day${days === 1 ? '' : 's'} ago` : ''}. Open the proposal below and countersign to fully execute.`
        : 'The client has signed. Open the proposal below and countersign to fully execute.',
      urgency: overdue ? 'red' : 'amber',
      action: null,
    }
  }

  if (stage === 'Proposal Posted') {
    const days = daysSince(opp.proposalDate)
    const overdue = days !== null && days > 7
    // A joint (two-signer) proposal can sit here fully partially signed — Contract Stage doesn't
    // advance to 'Pending Countersignature' until EVERY required signer has signed (see the
    // multi-signer gate in journey-respond.js), so 'Proposal Posted' covers both "nobody has
    // signed" and "1 of 2 signed" states. Without checking countersignStatus.signers this branch
    // used to say "No signature yet" even when someone genuinely had signed.
    const signers = countersignStatus && Array.isArray(countersignStatus.signers) ? countersignStatus.signers : []
    const signedCount = signers.filter(s => s.signed).length
    const partiallySigned = signers.length > 1 && signedCount > 0 && signedCount < signers.length
    const desc = partiallySigned
      ? `${signedCount} of ${signers.length} required signers has signed. Awaiting ${signers.filter(s => !s.signed).map(s => s.name).filter(Boolean).join(' & ') || 'the remaining signer'} before it's ready for countersignature.`
      : 'No signature yet. A check-in call or email keeps this moving.'
    return {
      step: 'awaiting-signature',
      title: overdue ? `Overdue: follow up — posted ${days} days ago, no response` : (partiallySigned ? 'Awaiting the remaining signer' : 'Proposal posted — awaiting the client'),
      desc,
      urgency: overdue ? 'red' : (partiallySigned ? 'amber' : (days !== null && days > 3 ? 'amber' : 'teal')),
      action: 'log', prefill: { opportunityType: 'Follow-Up' },
    }
  }

  // Early pursuit stages — momentum/cold-fallback, mirrors the Lead engine's own rules.
  const activityType = STAGE_ACTIVITY[stage] || 'Follow-Up'

  if (!ix.length) {
    const d = daysSince(opp.createdTime)
    return {
      step: 'first-touch',
      title: (d !== null && d > 3) ? `Overdue: no activity logged (${d} days)` : 'Log the first touch',
      desc: 'Nothing has been logged on this opportunity yet.',
      urgency: (d !== null && d > 3) ? 'red' : 'amber',
      action: 'log', prefill: { opportunityType: activityType },
    }
  }

  const days = daysSince(ix[0].date)
  if (days !== null && days > 14) {
    return {
      step: 'cold', title: `No activity in ${days} days`,
      desc: 'This opportunity has gone quiet. Re-engage or move it to No Go.',
      urgency: 'red', action: 'log', prefill: { opportunityType: activityType },
    }
  }
  if (days !== null && days > 7) {
    return {
      step: 'follow-up', title: `Log a follow-up — ${days} days since last touch`,
      desc: 'Keep this moving before it goes cold.',
      urgency: 'amber', action: 'log', prefill: { opportunityType: activityType },
    }
  }
  return {
    step: 'momentum',
    title: days !== null ? `Last contact ${days} day${days === 1 ? '' : 's'} ago` : 'Keep the momentum going',
    desc: 'Log the next touch to keep this moving.',
    urgency: 'teal', action: 'log', prefill: { opportunityType: activityType },
  }
}
